import React from 'react';
import {
  FaBoxOpen,
  FaCode,
  FaExternalLinkAlt,
  FaMicrochip,
  FaMobileAlt,
  FaRocket,
  FaVideo,
  FaWallet,
} from 'react-icons/fa';

const collectionIcons = {
  Projects: <FaRocket aria-hidden="true" />,
  Apps: <FaMobileAlt aria-hidden="true" />,
  'Open Source': <FaCode aria-hidden="true" />,
  IoT: <FaMicrochip aria-hidden="true" />,
  Blueprints: <FaBoxOpen aria-hidden="true" />,
};

function toYouTubeEmbed(url) {
  if (!url) {
    return '';
  }

  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${parsed.pathname.replace('/', '')}`;
    }

    if (parsed.hostname.includes('youtube.com')) {
      const videoId = parsed.searchParams.get('v');
      return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
    }
  } catch (error) {
    return '';
  }

  return '';
}

function ItemCard({ item }) {
  const statusSlug = item.status.toLowerCase().replace(/\s+/g, '-');
  const firstVideo = item.videos?.[0];
  const embedUrl = toYouTubeEmbed(firstVideo?.url);

  return (
    <article className="item-card" id={item.id}>
      {item.coverImageUrl ? (
        <div className="item-card__media">
          <img src={item.coverImageUrl} alt={item.title} />
        </div>
      ) : null}

      {embedUrl ? (
        <div className="item-card__video">
          <iframe
            src={embedUrl}
            title={`${item.title} video preview`}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : null}

      <div className="item-card__topline">
        <span className="pill pill--collection">
          {collectionIcons[item.collection] || <FaWallet aria-hidden="true" />}
          {item.collection}
        </span>
        <span className={`pill pill--status pill--status-${statusSlug}`}>
          {item.status}
        </span>
      </div>

      <h3>{item.title}</h3>
      <p>{item.summary}</p>

      {item.impact ? <div className="item-card__impact">{item.impact}</div> : null}

      {item.buildInstructions ? (
        <div className="item-card__build">
          <strong>Build / implementation notes</strong>
          <p>{item.buildInstructions}</p>
        </div>
      ) : null}

      {item.platforms?.length ? (
        <div className="chip-list chip-list--platforms">
          {item.platforms.map((platform) => (
            <span key={`${item.id}-${platform}`}>{platform}</span>
          ))}
        </div>
      ) : null}

      {item.priceLabel || item.availabilityNote ? (
        <div className="item-card__meta">
          {item.priceLabel ? <strong>{item.priceLabel}</strong> : null}
          {item.availabilityNote ? <span>{item.availabilityNote}</span> : null}
        </div>
      ) : null}

      {item.technologies?.length ? (
        <div className="chip-list">
          {item.technologies.map((technology) => (
            <span key={technology}>{technology}</span>
          ))}
        </div>
      ) : null}

      {item.links?.length ? (
        <div className="item-card__links">
          {item.links.map((link) => (
            <a
              key={`${item.id}-${link.label}-${link.url}`}
              href={link.url}
              target={link.url.startsWith('http') ? '_blank' : undefined}
              rel={link.url.startsWith('http') ? 'noreferrer' : undefined}
            >
              <span>{link.label}</span>
              <small>{link.type}</small>
              <FaExternalLinkAlt aria-hidden="true" />
            </a>
          ))}
        </div>
      ) : null}

      {!embedUrl && item.videos?.length ? (
        <div className="video-link-list">
          {item.videos.map((video) => (
            <a
              key={`${item.id}-${video.url}`}
              href={video.url}
              target="_blank"
              rel="noreferrer"
            >
              <FaVideo aria-hidden="true" />
              {video.title}
            </a>
          ))}
        </div>
      ) : null}
    </article>
  );
}

export default ItemCard;
